import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Card } from '@sorare/core/src/components/card/Card';
import { OpenItemDialogLink } from '@sorare/core/src/components/link/OpenItemDialogLink';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';

import {
  Placeholder,
  ResponsiveNumber,
  RightBlock,
} from '#src/components/invite/OnlineLanding/RightBlock/index.tsx';

import type { CardsEarned_currentUser } from './__generated__/index.graphql.ts';

const messages = defineMessages({
  title: {
    defaultMessage: 'Cards earned',
  },
  subtitle: {
    defaultMessage: 'Check out the cards you won over time',
  },
});

const CardList = styled(Vertical).attrs({ gap: 2 })`
  align-self: stretch;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
`;

const StyledResponsiveNumber = styled(ResponsiveNumber)`
  max-height: 380px;
`;

export const CardsEarned = ({
  currentUser,
  loading,
  loadMore,
}: {
  currentUser?: CardsEarned_currentUser | null;
  loading: boolean;
  loadMore: (variables: { after: string | null }) => void;
}) => {
  const total = currentUser?.cardsReferralRewards.totalCount || 0;

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore({
        after: currentUser?.cardsReferralRewards.pageInfo.endCursor || null,
      });
    },
    !!currentUser?.cardsReferralRewards.pageInfo.hasNextPage,
    loading
  );

  return (
    <RightBlock
      title={<FormattedMessage {...messages.title} />}
      subTitle={<FormattedMessage {...messages.subtitle} />}
      total={`${total}`}
      dialogProps={{
        title: <FormattedMessage {...messages.title} />,
        subtitle: <FormattedMessage {...messages.subtitle} />,
        counts: [
          {
            label: <FormattedMessage defaultMessage="Earned" />,
            count: `${total}`,
          },
        ],
        children: currentUser?.cardsReferralRewards.nodes?.length ? (
          <CardList>
            {currentUser.cardsReferralRewards.nodes.map(reward => {
              if (!reward.anyCard) return null;
              return (
                <OpenItemDialogLink
                  key={reward.id}
                  sport={reward.anyCard.sport}
                  item={reward.anyCard}
                >
                  <Card card={reward.anyCard} />
                </OpenItemDialogLink>
              );
            })}
            <InfiniteScrollLoader />
          </CardList>
        ) : (
          <Placeholder>
            <StyledResponsiveNumber total="0" alignmentBaseline="middle" />
          </Placeholder>
        ),
      }}
      loading={!currentUser}
    />
  );
};

CardsEarned.fragments = {
  currentUser: gql`
    fragment CardsEarned_currentUser on CurrentUser {
      slug
      cardsReferralRewards(sport: $sport, first: 6, after: $after) {
        nodes {
          id
          anyCard {
            slug
            assetId
            sport
            ...Card_anyCard
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
        totalCount
      }
    }
    ${Card.fragments.anyCard}
  ` as TypedDocumentNode<CardsEarned_currentUser>,
};
